import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronRight, ArrowUpRight } from "lucide-react";

import Seo, { breadcrumbLd } from "@/components/seo/Seo";
import Jar from "@/components/visual/Jar";
import NewsletterSection from "@/components/marketing/NewsletterSection";
import { BRAND } from "@/content/brand";
import { useSiteSettings } from "@/lib/siteSettings";

const SITE_URL =
  process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

/* ------------------------------------------------------------------ */
/* Shared building blocks (same visual language as Home.jsx)           */
/* ------------------------------------------------------------------ */

function PageSeo({ title, description, path, name, noindex = false }) {
  return (
    <>
      <Seo
        title={title}
        description={description}
        url={`${SITE_URL}${path}`}
        jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name, path }])}
      />
      {noindex && (
        <Helmet>
          <meta name="robots" content="noindex" />
        </Helmet>
      )}
    </>
  );
}

function SectionHead({ eyebrow, title, sub, dark = false, center = true }) {
  return (
    <div className={center ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
      {eyebrow && <p className={`font-body text-[15px] font-semibold ${dark ? "text-brand-bone/70" : "text-brand-gold"}`}>{eyebrow}</p>}
      <h2 className={`font-display mt-2 text-[clamp(2.2rem,5.5vw,4rem)] leading-[1.02] tracking-display ${dark ? "text-white" : "text-brand-ink"}`}>
        {title}
      </h2>
      {sub && <p className={`mt-5 font-body text-[17px] sm:text-[19px] leading-relaxed ${dark ? "text-brand-bone/70" : "text-ink-muted"}`}>{sub}</p>}
    </div>
  );
}

function DarkHero({ crumb, eyebrow, title, sub, children, jar = false }) {
  return (
    <section className="bg-brand-obsidian text-brand-bone overflow-hidden">
      <div className={`max-w-6xl mx-auto px-6 pt-16 sm:pt-24 pb-20 ${jar ? "grid lg:grid-cols-[1.4fr_1fr] gap-12 items-center" : ""}`}>
        <div className={jar ? "" : "max-w-3xl"}>
          <nav aria-label="Breadcrumb" className="font-body text-[13px] text-brand-bone/60">
            <Link to="/" className="hover:underline">Home</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span>{crumb}</span>
          </nav>
          {eyebrow && <p className="mt-6 font-body text-[15px] sm:text-[17px] font-semibold text-brand-bone/80">{eyebrow}</p>}
          <h1 className="font-display text-white mt-3 text-[clamp(2.6rem,7.5vw,5.75rem)] leading-[0.98] tracking-display">{title}</h1>
          {sub && <p className="mt-6 max-w-2xl font-body text-[17px] sm:text-[21px] leading-snug text-brand-bone/75">{sub}</p>}
          {children}
        </div>
        {jar && (
          <div className="mx-auto w-[170px] sm:w-[210px]">
            <Jar className="w-full h-auto" />
          </div>
        )}
      </div>
    </section>
  );
}

function Prose({ children }) {
  return <div className="space-y-5 font-body text-[17px] sm:text-[18px] leading-relaxed text-ink-muted">{children}</div>;
}

function ClosingCta({ title, sub, links = [] }) {
  return (
    <section className="bg-brand-obsidian text-center py-28 sm:py-32">
      <div className="max-w-3xl mx-auto px-6">
        <h2 className="font-display text-white text-[clamp(2.2rem,5.5vw,4rem)] leading-[1] tracking-display">{title}</h2>
        {sub && <p className="mt-6 font-body text-[19px] text-brand-bone/70">{sub}</p>}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
          <Link to="/products" className="inline-flex items-center rounded-control bg-brand-gold hover:bg-brand-gold-deep text-white font-body text-[17px] px-7 py-3.5 transition-colors">
            Shop Karijeeva
          </Link>
          <Link to="/lab-reports" className="inline-flex items-center gap-1 font-body text-[17px] text-white hover:underline">
            See the lab report <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        {links.length > 0 && (
          <ul className="mt-12 flex flex-wrap justify-center gap-3">
            {links.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="inline-flex rounded-control bg-brand-obsidian-soft px-4 py-2 font-body text-[14px] text-brand-bone hover:text-white">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function OriginCards() {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {BRAND.origins.map((o) => (
        <div key={o.key} className="rounded-3xl bg-brand-obsidian-soft p-10 min-h-[300px] flex flex-col">
          <p className="font-body text-[13px] font-semibold text-brand-bone/60">{o.share}</p>
          <h3 className="font-display text-white text-[40px] leading-none tracking-heading mt-3">{o.name}</h3>
          <p className="font-body text-brand-bone/60 mt-2">{o.region}</p>
          <p className="font-body text-[17px] leading-relaxed text-brand-bone/80 mt-auto pt-10">{o.line}</p>
        </div>
      ))}
    </div>
  );
}

/**
 * An abstract sketch of Karnataka: not a real outline and not to scale.
 * It only shows that one origin is inland and the other is on the coast.
 */
function OriginSketch() {
  return (
    <figure className="rounded-3xl bg-white p-6 sm:p-10">
      <svg viewBox="0 0 400 420" className="w-full h-auto max-w-[420px] mx-auto" role="img" aria-label="Sketch of Karnataka showing Tiptur inland and the Uttara Kannada coast from Gokarna to Shirali. Not to scale.">
        <rect x="0" y="0" width="400" height="420" rx="28" fill="#e9f0f2" />
        <text x="30" y="395" fontSize="13" fill="#6b7a80" fontFamily="inherit">Arabian Sea</text>
        <path
          d="M150 30 C 230 20, 330 50, 360 120 C 385 185, 350 260, 320 320 C 295 370, 230 400, 185 390 C 150 382, 135 350, 120 310 C 105 265, 95 215, 92 170 C 90 110, 105 45, 150 30 Z"
          fill="#f4efe4"
          stroke="#c9b98f"
          strokeWidth="2"
        />
        <path d="M100 150 C 104 185, 110 210, 116 240" fill="none" stroke="#b8862b" strokeWidth="7" strokeLinecap="round" />
        <circle cx="100" cy="150" r="5" fill="#b8862b" />
        <circle cx="116" cy="240" r="5" fill="#b8862b" />
        <text x="126" y="146" fontSize="14" fill="#1d1d1f" fontFamily="inherit">Gokarna</text>
        <text x="130" y="246" fontSize="14" fill="#1d1d1f" fontFamily="inherit">Shirali</text>
        <text x="126" y="198" fontSize="12" fill="#6b7a80" fontFamily="inherit">Uttara Kannada coast</text>
        <circle cx="255" cy="290" r="9" fill="#b8862b" />
        <text x="228" y="318" fontSize="14" fill="#1d1d1f" fontFamily="inherit">Tiptur</text>
        <text x="206" y="336" fontSize="12" fill="#6b7a80" fontFamily="inherit">Tumakuru district</text>
        <text x="220" y="110" fontSize="16" fill="#6b7a80" fontFamily="inherit">Karnataka</text>
      </svg>
      <figcaption className="mt-4 text-center font-body text-[13px] text-ink-muted">A simple sketch, not a map. Shapes and distances are not to scale.</figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/* The Farm                                                            */
/* ------------------------------------------------------------------ */

export function TheFarm() {
  return (
    <>
      <PageSeo
        path="/the-farm"
        name="The farm"
        title="Our Coconut Farms in Karnataka"
        description="Karijeeva coconuts grow on our family farms in Tiptur and on the Uttara Kannada coast from Gokarna to Shirali. See what each place is like and why it matters."
      />
      <div data-testid="page-the-farm">
        <DarkHero
          crumb="The farm"
          eyebrow="Where it grows"
          title={<>Our coconuts.<br />Our farms.</>}
          sub="Every coconut we press grows on farms our family owns in Karnataka. Most come from Tiptur, and the rest from the Uttara Kannada coast between Gokarna and Shirali."
        />

        <section className="bg-brand-obsidian text-brand-bone pb-24 sm:pb-32">
          <div className="max-w-6xl mx-auto px-6">
            <OriginCards />
          </div>
        </section>

        <section className="bg-brand-parchment py-24 sm:py-32">
          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <SectionHead center={false} eyebrow="Inland" title="What is Tiptur like?" />
              <div className="mt-6">
                <Prose>
                  <p>
                    Tiptur is a town in Tumakuru district, a few hours' drive northwest of Bengaluru. It has long been known across Karnataka for its coconuts, and its market is one of the best known copra markets in India.
                  </p>
                  <p>
                    Karnataka grows more coconuts than any other state in India, and Tumakuru district is one of its coconut heartlands. Most of the coconuts in a jar of Karijeeva grow here.
                  </p>
                </Prose>
              </div>
            </div>
            <OriginSketch />
          </div>
        </section>

        <section className="bg-brand-parchment-soft py-24 sm:py-32">
          <div className="max-w-3xl mx-auto px-6">
            <SectionHead center={false} eyebrow="On the coast" title="What is the Gokarna to Shirali coast like?" />
            <div className="mt-6">
              <Prose>
                <p>
                  Our coastal farms sit on the Uttara Kannada coast, from the temple town of Gokarna down to Shirali near Bhatkal. Here the Western Ghats come close to the Arabian Sea, leaving a narrow strip of land between the hills and the water.
                </p>
                <p>
                  The monsoon arrives hard on this coast and brings heavy rain every year. Palms here grow in sea air and monsoon rain. It is a very different place from Tiptur, and we are glad to farm both.
                </p>
              </Prose>
            </div>
          </div>
        </section>

        <section className="bg-brand-parchment py-24 sm:py-32">
          <div className="max-w-6xl mx-auto px-6">
            <SectionHead eyebrow="Why it matters" title="Why grow our own coconuts?" sub="Buying oil in bulk and putting our label on it would be easier. Here is why we don't." />
            <div className="mt-14 grid md:grid-cols-3 gap-4">
              {[
                {
                  title: "We know where it came from",
                  body: "Because the coconuts are ours, we can tell you which of our two regions your oil came from. No trader in between, no mystery supplier.",
                },
                {
                  title: "Small batches",
                  body: "We press in small batches and test each one at an independent food testing lab before it is packed. That is only practical when you control the coconuts.",
                },
                {
                  title: "We can't buy our way to more",
                  body: "We make only as much oil as our farms grow. Some batches sell out, and we would rather wait than fill jars with coconuts we can't vouch for.",
                },
              ].map((c) => (
                <div key={c.title} className="rounded-3xl bg-white p-8 min-h-[240px] flex flex-col">
                  <h3 className="font-display text-[26px] leading-tight tracking-heading text-brand-ink">{c.title}</h3>
                  <p className="font-body text-[16px] leading-relaxed text-ink-muted mt-4">{c.body}</p>
                </div>
              ))}
            </div>
            <p className="mt-10 text-center">
              <Link to="/cold-press-process" className="inline-flex items-center gap-1 font-body text-[17px] text-brand-gold hover:underline">
                What happens after harvest <ChevronRight className="w-4 h-4" />
              </Link>
            </p>
          </div>
        </section>

        <ClosingCta
          title="Coconut oil you can trace to the farm."
          sub="One ingredient. Two Karnataka origins. Every batch tested."
          links={[
            { to: "/about", label: "Our story" },
            { to: "/cold-press-process", label: "How we make it" },
            { to: "/cold-pressed-vs-refined-coconut-oil", label: "Cold pressed vs refined" },
          ]}
        />
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Cold press process                                                  */
/* ------------------------------------------------------------------ */

const STEPS = [
  { title: "Harvest", body: "Mature coconuts are harvested from our family farms in Tiptur and on the Uttara Kannada coast." },
  { title: "Dry", body: "The coconuts are dehusked and split, and the kernel, the white flesh inside, is dried into copra." },
  { title: "Press", body: "The copra is pressed mechanically, with the temperature controlled so no heat is added. Nothing is added to help the oil out." },
  { title: "Settle and filter", body: "The pressed oil is left to settle, then filtered so it is clear and clean." },
  { title: "Test", body: "A sample from the batch goes to an independent food testing lab before the oil is packed." },
  { title: "Jar", body: "The oil goes into jars and every jar carries a batch number, so you can ask us for that batch's report." },
];

const NEVER = [
  { title: "Refine", body: "No refining with heat and chemicals to make the oil neutral." },
  { title: "Bleach", body: "No bleaching to strip out colour." },
  { title: "Deodorise", body: "No deodorising, so the oil keeps its natural coconut aroma." },
  { title: "Blend", body: "No mixing with palm, groundnut or any other oil." },
  { title: "Add", body: "No added colour, no added aroma. The only ingredient is coconut." },
];

export function ColdPressProcess() {
  return (
    <>
      <PageSeo
        path="/cold-press-process"
        name="How we make it"
        title="How We Make Cold Pressed Coconut Oil"
        description="How Karijeeva goes from our coconut farms to your jar: harvest, press without added heat, settle, filter, lab test, jar. Never refined, bleached, blended."
      />
      <div data-testid="page-cold-press-process">
        <DarkHero
          crumb="How we make it"
          eyebrow="From farm to jar"
          title={<>Pressed.<br />Filtered.<br />Tested.</>}
          sub="Making cold pressed coconut oil is simple when you leave out the refining. Here is every step, in order."
          jar
        />

        <section className="bg-brand-parchment py-24 sm:py-32">
          <div className="max-w-6xl mx-auto px-6">
            <SectionHead eyebrow="Six steps" title="How is Karijeeva made?" />
            <ol className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {STEPS.map((s, i) => (
                <li key={s.title} className="rounded-3xl bg-white p-8 min-h-[220px] flex flex-col">
                  <p className="font-display text-[56px] leading-none tracking-display text-brand-gold">{i + 1}</p>
                  <h3 className="font-display text-[24px] tracking-heading text-brand-ink mt-4">{s.title}</h3>
                  <p className="font-body text-[16px] leading-relaxed text-ink-muted mt-3">{s.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="bg-brand-obsidian text-brand-bone py-24 sm:py-32">
          <div className="max-w-6xl mx-auto px-6">
            <SectionHead
              dark
              eyebrow="What we leave out"
              title="What do we never do?"
              sub="Refined coconut oil (often sold as RBD: refined, bleached, deodorised) goes through extra steps to make it neutral and cheap. We skip all of them."
            />
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {NEVER.map((n) => (
                <div key={n.title} className="rounded-3xl bg-brand-obsidian-soft p-7 min-h-[200px] flex flex-col">
                  <p className="font-body text-[13px] font-semibold text-brand-bone/60">We never</p>
                  <h3 className="font-display text-white text-[28px] tracking-heading mt-2">{n.title}</h3>
                  <p className="font-body text-[15px] leading-relaxed text-brand-bone/75 mt-auto pt-6">{n.body}</p>
                </div>
              ))}
            </div>
            <p className="mt-10 text-center">
              <Link to="/cold-pressed-vs-refined-coconut-oil" className="inline-flex items-center gap-1 font-body text-[17px] text-white hover:underline">
                Cold pressed vs refined coconut oil <ChevronRight className="w-4 h-4" />
              </Link>
            </p>
          </div>
        </section>

        <section className="bg-brand-parchment-soft py-24 sm:py-32">
          <div className="max-w-3xl mx-auto px-6">
            <SectionHead center={false} eyebrow="A note on what we publish" title="Why this page is short." />
            <div className="mt-6">
              <Prose>
                <p>
                  You may read long descriptions of machines and temperatures on other coconut oil labels. We only put on this page what we can show you, and the lab report is the part you can check for yourself.
                </p>
                <p>
                  If you want to know more about the batch in your kitchen, write to <a href="mailto:support@karijeeva.in" className="text-brand-gold hover:underline">support@karijeeva.in</a> with the batch number on the label and we will send you its lab report.
                </p>
              </Prose>
            </div>
          </div>
        </section>

        <ClosingCta
          title="One ingredient. Nothing taken out."
          sub="Pure cold pressed coconut oil from our family farms."
          links={[
            { to: "/the-farm", label: "Our farms" },
            { to: "/how-to-check-coconut-oil-purity", label: "Check any coconut oil's purity" },
            { to: "/cooking-with-coconut-oil", label: "Cooking with coconut oil" },
          ]}
        />
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Sustainability                                                      */
/* ------------------------------------------------------------------ */

export function Sustainability() {
  return (
    <>
      <PageSeo
        path="/sustainability"
        name="Sustainability"
        title="Sustainability: What We Can Say"
        description="What we can honestly say about how Karijeeva is made: one ingredient, small batches, our family farms, nothing refined. We will share more when we can measure it."
      />
      <div data-testid="page-sustainability">
        <DarkHero
          crumb="Sustainability"
          eyebrow="Sustainability"
          title={<>What we can say.<br />And what we can't, yet.</>}
          sub="Plenty of brands make big green promises. We would rather tell you only what we know."
        />

        <section className="bg-brand-parchment py-24 sm:py-32">
          <div className="max-w-6xl mx-auto px-6">
            <SectionHead eyebrow="What we know" title="Four things that are true today." />
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { big: "1", unit: "ingredient", body: "Coconut. Nothing blended in and nothing added." },
                { big: "0", unit: "refining steps", body: "No refining, bleaching or deodorising, so none of the extra processing those steps need." },
                { big: "2", unit: "origins we farm", body: "Our coconuts come from our family farms in Tiptur and on the Uttara Kannada coast." },
                { big: "Small", unit: "batches", body: "We make only as much oil as our farms grow." },
              ].map((h) => (
                <div key={h.unit} className="rounded-3xl bg-white p-8 min-h-[240px] flex flex-col">
                  <p className="font-display text-[56px] leading-none tracking-display text-brand-ink">{h.big}</p>
                  <p className="font-display text-[20px] mt-2 text-brand-ink">{h.unit}</p>
                  <p className="font-body text-[15px] text-ink-muted mt-auto pt-6 leading-relaxed">{h.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-brand-parchment-soft py-24 sm:py-32">
          <div className="max-w-3xl mx-auto px-6">
            <SectionHead center={false} eyebrow="What we don't know yet" title="We'll publish more when we can measure it." />
            <div className="mt-6">
              <Prose>
                <p>
                  We have not measured our water use, carbon footprint or packaging impact, so we won't put numbers or badges on this page. Guessing would not be fair to you.
                </p>
                <p>
                  When we have real measurements, we will share them here along with how we got them. If there is something you would like us to look at first, tell us at <a href="mailto:support@karijeeva.in" className="text-brand-gold hover:underline">support@karijeeva.in</a>.
                </p>
              </Prose>
            </div>
          </div>
        </section>

        <ClosingCta
          title="Simple oil, made simply."
          links={[
            { to: "/the-farm", label: "Our farms" },
            { to: "/cold-press-process", label: "How we make it" },
          ]}
        />
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Press                                                               */
/* ------------------------------------------------------------------ */

export function Press() {
  const settings = useSiteSettings();
  const logos = Array.isArray(settings.press_logos) ? settings.press_logos.filter((l) => l && l.name) : [];
  return (
    <>
      <PageSeo
        path="/press"
        name="Press"
        title="Press and Media Enquiries"
        description="Karijeeva is a new cold pressed coconut oil brand from Karnataka. Journalists can reach us at press@karijeeva.in for facts, samples and lab reports on request."
      />
      <div data-testid="page-press">
        <DarkHero
          crumb="Press"
          eyebrow="Press"
          title={<>We're a new brand.</>}
          sub="Karijeeva is a young brand of Kadle Global Pvt Ltd. If you are writing about coconut oil, food safety or farming in Karnataka, we'd be glad to help."
        >
          <div className="mt-8">
            <a href="mailto:press@karijeeva.in" className="inline-flex items-center rounded-control bg-brand-gold hover:bg-brand-gold-deep text-white font-body text-[15px] px-6 py-3 transition-colors" data-testid="press-email">
              Email press@karijeeva.in
            </a>
          </div>
        </DarkHero>

        {logos.length > 0 && (
          <section className="bg-brand-parchment-soft py-20" data-testid="press-logos">
            <div className="max-w-6xl mx-auto px-6">
              <SectionHead eyebrow="Coverage" title="Where we've been written about." />
              <ul className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {logos.map((l) => (
                  <li key={l.name} className="rounded-3xl bg-white p-8">
                    <p className="font-display text-[24px] tracking-[-0.02em] text-brand-ink">{l.name}</p>
                    {l.url && (
                      <a href={l.url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1 font-body text-[15px] text-brand-gold hover:underline">
                        Read the article <ArrowUpRight className="w-4 h-4" />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        <section className="bg-brand-parchment py-24 sm:py-32">
          <div className="max-w-6xl mx-auto px-6">
            <SectionHead eyebrow="For journalists" title="What we can share." />
            <div className="mt-14 grid md:grid-cols-3 gap-4">
              {[
                { title: "The facts", body: "Where our coconuts grow, how the oil is made, and what we do and don't claim about it." },
                { title: "Lab reports", body: `Our latest lab report is public, and we test every batch. ${BRAND.batchesTested} batches so far.`, to: "/lab-reports", cta: "See the lab report" },
                { title: "Samples", body: "Ask us and tell us what you are working on. We will reply as soon as we can." },
              ].map((c) => (
                <div key={c.title} className="rounded-3xl bg-white p-8 min-h-[220px] flex flex-col">
                  <h3 className="font-display text-[26px] tracking-heading text-brand-ink">{c.title}</h3>
                  <p className="font-body text-[16px] leading-relaxed text-ink-muted mt-3">{c.body}</p>
                  {c.to && (
                    <Link to={c.to} className="mt-auto pt-6 inline-flex items-center gap-1 font-body text-[15px] text-brand-gold hover:underline">
                      {c.cta} <ChevronRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-10 text-center">
              <Link to="/about" className="inline-flex items-center gap-1 font-body text-[17px] text-brand-gold hover:underline">
                Read our story <ChevronRight className="w-4 h-4" />
              </Link>
            </p>
          </div>
        </section>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Careers                                                             */
/* ------------------------------------------------------------------ */

export function Careers() {
  const settings = useSiteSettings();
  const email = settings.careers_email || "hello@karijeeva.in";
  return (
    <>
      <PageSeo
        path="/careers"
        name="Careers"
        title="Careers: Work With Us"
        description="Karijeeva is a small team making cold pressed coconut oil from our family farms in Karnataka. We have no open roles listed right now, but you are welcome to write."
      />
      <div data-testid="page-careers">
        <DarkHero
          crumb="Careers"
          eyebrow="Careers"
          title={<>Small team.<br />Honest work.</>}
          sub="We grow coconuts, press them, test every batch and sell the oil across India. We have no open roles listed right now."
        />
        <section className="bg-brand-parchment py-24 sm:py-32">
          <div className="max-w-3xl mx-auto px-6">
            <div className="rounded-3xl bg-white p-8 sm:p-12">
              <h2 className="font-display text-[clamp(1.9rem,4vw,2.75rem)] leading-[1.05] tracking-heading text-brand-ink">Want to work with us?</h2>
              <p className="mt-4 font-body text-[17px] leading-relaxed text-ink-muted">
                If you care about good food and plain, honest selling, write to us. Tell us a little about yourself and what you would like to do. When a role opens, we will read every note we have received.
              </p>
              <a href={`mailto:${email}?subject=${encodeURIComponent("Working at Karijeeva")}`} className="mt-8 inline-flex items-center rounded-control bg-brand-gold hover:bg-brand-gold-deep text-white font-body text-[15px] px-6 py-3 transition-colors" data-testid="careers-email">
                Email {email}
              </a>
            </div>
            <p className="mt-10 text-center">
              <Link to="/about" className="inline-flex items-center gap-1 font-body text-[17px] text-brand-gold hover:underline">
                Read our story <ChevronRight className="w-4 h-4" />
              </Link>
            </p>
          </div>
        </section>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Gift cards and Subscribe & save: not available yet                  */
/* ------------------------------------------------------------------ */

function NotYet({ testid, path, name, title, description, heading, sub }) {
  return (
    <>
      <PageSeo path={path} name={name} title={title} description={description} noindex />
      <div data-testid={testid}>
        <DarkHero crumb={name} eyebrow={name} title={heading} sub={sub}>
          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
            <Link to="/products" className="inline-flex items-center rounded-control bg-brand-gold hover:bg-brand-gold-deep text-white font-body text-[15px] px-6 py-3 transition-colors">
              Shop the oil
            </Link>
            <a href="#newsletter" className="inline-flex items-center gap-1 font-body text-[15px] text-brand-bone hover:underline">
              Get our emails <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </DarkHero>
        <div id="newsletter" className="scroll-mt-16">
          <NewsletterSection />
        </div>
      </div>
    </>
  );
}

export function GiftCards() {
  return (
    <NotYet
      testid="page-gift-cards"
      path="/gift-cards"
      name="Gift cards"
      title="Gift Cards: Not Available Yet"
      description="Karijeeva gift cards are not available yet. You can still send a jar of our cold pressed coconut oil, or join our emails to hear when anything new launches."
      heading={<>Gift cards aren't available yet.</>}
      sub="We don't sell gift cards today. If you'd like to send someone a jar, you can order it from the shop and ship it to their address anywhere in India."
    />
  );
}

const REFILL_STEPS = [
  ["Order as usual", "Choose your jar or family pack and check out. On the review step, tick Remind me to refill and pick every 30, 45 or 60 days."],
  ["We message you", "When it is nearly time, we send you a WhatsApp message with a link that puts the same order in your basket."],
  ["One tap to reorder", "Open the link, check the basket, pay. Change the size, skip a month or stop by simply not ordering."],
];

const REFILL_FAQ = [
  ["Will you take money automatically?", "No. There is no auto debit and no mandate. You only pay when you choose to reorder, the same way as any other order."],
  ["How do I pick the interval?", "Think about how long your last jar lasted. If a litre goes in about a month, pick 30 days. If you use it mostly for hair or skin, 60 days usually suits better."],
  ["Can I change or stop it?", "Yes. Reply to the reminder to change the interval, or tell us to stop and we will. You are never locked in."],
  ["Is the price the same?", "Refill orders are charged at the price shown on the site on the day you reorder."],
];

export function MonthlyRefill() {
  return (
    <>
      <PageSeo
        path="/monthly-refill"
        name="Monthly refill"
        title="Monthly Coconut Oil Refill, Without Auto Debit"
        description="Never run out of cold pressed coconut oil. Tick Remind me to refill at checkout and Karijeeva messages you on WhatsApp with a one tap reorder link. No auto debit, stop any time."
      />
      <div data-testid="page-monthly-refill">
        <DarkHero
          crumb="Monthly refill"
          eyebrow="Monthly refill"
          title={<>Never run out.<br />Never get charged by surprise.</>}
          sub="A reminder when your oil is running low, with a link that reorders it in one tap. No auto debit, no lock in."
        >
          <div className="mt-8">
            <Link to="/products" className="inline-flex items-center rounded-control bg-brand-gold hover:bg-brand-gold-deep text-white font-body text-[15px] px-6 py-3 transition-colors">
              Choose your oil
            </Link>
          </div>
        </DarkHero>

        <section className="bg-brand-parchment py-24">
          <div className="max-w-5xl mx-auto px-6">
            <SectionHead eyebrow="How it works" title="Three steps, one of them is ours." />
            <ol className="mt-12 grid md:grid-cols-3 gap-4">
              {REFILL_STEPS.map(([t, b], i) => (
                <li key={t} className="rounded-3xl bg-white p-8">
                  <p className="font-body text-[14px] font-semibold text-brand-gold">Step {i + 1}</p>
                  <p className="mt-2 font-display text-[22px] tracking-[-0.02em] text-brand-ink">{t}</p>
                  <p className="mt-3 font-body text-[15px] leading-relaxed text-ink-muted">{b}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="bg-brand-parchment-soft py-24">
          <div className="max-w-3xl mx-auto px-6">
            <SectionHead eyebrow="Questions" title="Before you tick the box." />
            <dl className="mt-10 divide-y divide-black/10 border-y border-black/10">
              {REFILL_FAQ.map(([q, a]) => (
                <div key={q} className="py-6">
                  <dt className="font-display text-[19px] tracking-[-0.01em] text-brand-ink">{q}</dt>
                  <dd className="mt-2 font-body text-[16px] leading-relaxed text-ink-muted">{a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      </div>
    </>
  );
}
